import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import modelMappingService from '../services/model-mapping/model-mapping-service.js';
import useModelMappingStore from '../stores/model-mapping-store.js';
import Swal from 'sweetalert2';

// ==================== QUERIES (READ OPERATIONS) ====================

export const useAvailableLayers = () => {
  return useQuery({
    queryKey: ['model-mapping', 'layers'],
    queryFn: () => modelMappingService.getFeatureLayers(),
    staleTime: Infinity, // Layers don't change
    gcTime: 1000 * 60 * 60, // Keep in cache for 1 hour
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });
};

export const useFeatureData = (filename, taz) => {
  return useQuery({
    queryKey: ['model-mapping', 'feature', filename, taz],
    queryFn: () => modelMappingService.getFeatureData(filename, taz),
    enabled: !!taz && !!filename, // Only fetch when both are available
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 10, // Keep in cache for 10 minutes
    select: (data) => {
      // Transform API response to match expected structure
      return {
        filename: filename,
        taz: taz,
        current_version: data.current_version,
        version_history: data.version_history,
        property_definitions: data.property_definitions,
        feature_header_id: data.feature_header_id,
      };
    },
  });
};

export const usePendingFeatures = (filename, options = {}) => {
  return useQuery({
    queryKey: ['model-mapping', 'pending', filename],
    queryFn: () => modelMappingService.getPendingFeatures(filename),
    enabled: !!filename,
    staleTime: 1000 * 60 * 2, // 2 minutes (admins need fresh data)
    gcTime: 1000 * 60 * 5, // Keep in cache for 5 minutes
    select: (data) => {
      // Transform API response
      return {
        features: data.features || [],
        property_definitions: data.property_definitions || [],
      };
    },
    ...options, // Allow overriding options (e.g., enabled)
  });
};

// ==================== MUTATIONS (WRITE OPERATIONS) ====================

export const useUpdateFeature = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (updateData) => modelMappingService.updateFeature(updateData),
    onSuccess: (data, variables) => {
      // Invalidate feature query to refetch with new version
      queryClient.invalidateQueries({
        queryKey: ['model-mapping', 'feature', variables.filename, variables.taz],
      });

      // Invalidate pending features (new version created)
      queryClient.invalidateQueries({
        queryKey: ['model-mapping', 'pending', variables.filename],
      });

      Swal.fire({
        icon: 'success',
        title: 'Success',
        text: 'Feature updated successfully',
        timer: 2000,
        timerProgressBar: true,
      });
    },
    onError: (error) => {
      // Extract error message from BaseService error structure
      const errorMessage = error.message || error.data?.error || error.data?.message || 'Feature update failed';

      Swal.fire({
        icon: 'error',
        title: 'Update Failed',
        text: errorMessage,
      });
    },
  });
};

export const useSuperviseFeature = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (supervisionData) => modelMappingService.superviseFeature(supervisionData),
    onSuccess: (data, variables) => {
      // Invalidate pending features list
      queryClient.invalidateQueries({
        queryKey: ['model-mapping', 'pending', variables.filename],
      });

      // Invalidate the specific feature data (supervision updates current version)
      queryClient.invalidateQueries({
        queryKey: ['model-mapping', 'feature', variables.filename, variables.taz],
      });

      Swal.fire({
        icon: 'success',
        title: 'Success',
        text: 'Feature supervised successfully',
        timer: 2000,
        timerProgressBar: true,
      });

      console.log('✅ Feature supervision completed');
    },
    onError: (error) => {
      // Extract error message from BaseService error structure
      const errorMessage = error.message || error.data?.error || error.data?.message || 'Feature update failed';

      Swal.fire({
        icon: 'error',
        title: 'Update Failed',
        text: errorMessage,
      });
    },
  });
};